/**
 * @file standard.ts
 * @description Стартовый шаблон сайта «Стандартный» (phase19.28 этап А,
 * v0.19.63; PL6 «Стартовый сайт из шаблона»; проект uiux-starter-site
 * §2.1–2.4 и §3). Решения пользователя 2026-10-24 («всё по рекомендации»):
 * страницы «Документы» нет — пункт подвала «Документы» ведёт на
 * платформенную `/legal` (SB141); H1 главной — название магазина; шапка
 * «Каталог · Доставка и оплата · Контакты» (уточнение SB76); в «Где нас
 * найти» — вид контакта «Часы работы».
 *
 * Тема — `DEFAULT_SITE_THEME` без изменений (SB4): шаблон задаёт только
 * страницы, секции и меню. Все страницы — черновики (PL8), это решает сервер.
 */

import type { SectionContactKind } from "../site-theme/contacts-derived.js";
import { deepFreeze } from "../site-theme/deep-freeze.js";
import { STARTER_FAQ_DELIVERY, STARTER_FAQ_RETURNS, STARTER_HINT as H, STARTER_TEXT as T } from "./standard-texts.js";
import type { SiteStarterTemplate, StarterPatchOp, StarterSection, StarterTarget } from "./types.js";

const CATALOG: StarterTarget = { kind: "catalog" };
const DELIVERY_PAGE: StarterTarget = { kind: "page", slug: "delivery" };
const RETURNS_PAGE: StarterTarget = { kind: "page", slug: "returns" };
const CONTACTS_PAGE: StarterTarget = { kind: "page", slug: "contacts" };
const RETURNS_DOC: StarterTarget = { kind: "legal", slug: "returns-notice" };

/** Связь — все виды; на сайте показываются только заполненные (SB21). */
const REACH_KINDS: readonly SectionContactKind[] = ["phone", "email", "telegram", "whatsapp", "vk", "max"];

/** «Остались вопросы?» в конце страницы: контакты как на главной. */
const questions: StarterSection = {
  preset: "questions-left",
  patch: [{ op: "set-contacts", id: "el-1-3", kinds: REACH_KINDS }],
};

/** Первый экран внутренней страницы: без надписи «Раздел», H1 и строка о странице. */
function innerHero(title: string, about: string): StarterSection {
  return {
    preset: "hero-text-center-color",
    patch: [
      { op: "remove", id: "el-1-1" },
      { op: "set-text", id: "el-1-2", text: title },
      { op: "set-text", id: "el-1-3", text: about },
    ],
  };
}

/** Тексты колонок «Доставка · Оплата · Возврат» (delivery-payment-3col). */
const serviceTexts = (delivery: string): StarterPatchOp[] => [
  { op: "set-text", id: "el-1-3", text: delivery },
  { op: "set-text", id: "el-2-3", text: H.servicePayment },
  { op: "set-text", id: "el-3-3", text: H.serviceReturns },
];

/** Шаблон заморожен: общий для всех магазинов, сборка его не меняет. */
export const STANDARD_SITE_STARTER: SiteStarterTemplate = deepFreeze<SiteStarterTemplate>({
  key: "standard",
  version: 1,
  title: "Стандартный",
  pages: [
    {
      // §2.1 Главная.
      slug: "home",
      title: "Главная",
      kind: "SYSTEM_HOME",
      sections: [
        {
          preset: "hero-photo-left",
          patch: [
            { op: "set-text", id: "el-2-1", text: H.homeEyebrow },
            { op: "set-shop-name", id: "el-2-2" },
            { op: "set-text", id: "el-2-3", text: H.homeAbout },
            { op: "set-button", id: "el-2-4", label: T.toCatalog, target: CATALOG },
          ],
        },
        // Источник auto — дочерние категории «Товары витрины»; пусто — секция скрыта (SB21).
        { preset: "tiles-3-categories", patch: [] },
        { preset: "products-new-with-header", patch: [] },
        {
          preset: "delivery-payment-3col",
          patch: [
            { op: "set-header", title: T.howToBuy },
            ...serviceTexts(H.serviceDeliveryShort),
            // В заготовке «Подробнее» ведут в каталог — здесь на свои страницы.
            { op: "set-button", id: "el-1-4", target: DELIVERY_PAGE },
            { op: "set-button", id: "el-2-4", target: DELIVERY_PAGE },
            { op: "set-button", id: "el-3-4", target: RETURNS_PAGE },
          ],
        },
        questions,
      ],
    },
    {
      // §2.2 Доставка и оплата — страница условий (deliveryTermsPageId, SB86).
      slug: "delivery",
      title: T.deliveryTitle,
      kind: "CUSTOM",
      sections: [
        innerHero(T.deliveryTitle, T.deliveryAbout),
        {
          preset: "delivery-payment-3col",
          patch: [
            // H3 → H2: иначе уровни идут H1 → H3 (SB49).
            { op: "set-level", id: "el-1-2", level: 2 },
            { op: "set-level", id: "el-2-2", level: 2 },
            { op: "set-level", id: "el-3-2", level: 2 },
            ...serviceTexts(H.serviceDeliveryFull),
            { op: "remove", id: "el-1-4" },
            { op: "remove", id: "el-2-4" },
            { op: "set-button", id: "el-3-4", label: T.aboutReturnsLink, target: RETURNS_PAGE },
          ],
        },
        { preset: "faq", patch: [{ op: "set-faq", id: "el-1-1", items: STARTER_FAQ_DELIVERY }] },
        questions,
      ],
    },
    {
      // §2.3 Возврат и обмен.
      slug: "returns",
      title: T.returnsTitle,
      kind: "CUSTOM",
      sections: [
        innerHero(T.returnsTitle, H.returnsAbout),
        {
          preset: "empty-1",
          patch: [
            { op: "append", column: 0, element: { kind: "heading", level: 2, text: T.fullTerms } },
            { op: "append", column: 0, element: { kind: "text", text: T.fullTermsAbout } },
            {
              op: "append",
              column: 0,
              element: { kind: "button", label: T.openReturnsDoc, variant: "outline", target: RETURNS_DOC },
            },
          ],
        },
        { preset: "faq", patch: [{ op: "set-faq", id: "el-1-1", items: STARTER_FAQ_RETURNS }] },
        questions,
      ],
    },
    {
      // §2.4 Контакты; реквизиты не повторяются — они в полосе подвала (SB19).
      slug: "contacts",
      title: T.contactsTitle,
      kind: "CUSTOM",
      sections: [
        innerHero(T.contactsTitle, T.contactsAbout),
        {
          preset: "contacts",
          patch: [
            // «Контакты» → «Как связаться»: не повторять H1.
            { op: "set-text", id: "el-1-1", text: T.howToReach },
            { op: "set-contacts", id: "el-2-1", kinds: REACH_KINDS },
          ],
        },
        {
          preset: "where-to-find",
          patch: [
            { op: "set-text", id: "el-1-1", text: T.whereToFindTitle },
            { op: "set-text", id: "el-1-2", text: H.whereToFind },
            // Режим работы на странице контактов — ЗоЗПП ст. 9, SB118.
            { op: "set-contacts", id: "el-2-1", kinds: ["address", "hours"] },
          ],
        },
      ],
    },
  ],
  headerMenu: [
    { label: "Каталог", target: CATALOG },
    { label: T.deliveryTitle, target: DELIVERY_PAGE },
    { label: T.contactsTitle, target: CONTACTS_PAGE },
  ],
  footerMenu: [
    {
      title: null,
      inPhoneMenu: true,
      items: [
        { label: T.deliveryTitle, target: DELIVERY_PAGE },
        { label: T.returnsTitle, target: RETURNS_PAGE },
        { label: T.contactsTitle, target: CONTACTS_PAGE },
        { label: T.documents, target: { kind: "platform-legal" } },
      ],
    },
  ],
  deliveryTermsPageSlug: "delivery",
});
